import { useCallback, useEffect, useRef } from 'react';

export const CHAT_RESPONSE_TIMEOUT_MS = 45_000;

export function useChatMessageDelivery(defaultTimeoutMs = CHAT_RESPONSE_TIMEOUT_MS) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeUserMessageIdRef = useRef<string | null>(null);

  const clearDeliveryTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const beginDelivery = useCallback(
    (
      userMessageId: string,
      onTimeout: () => void,
      timeoutMs = defaultTimeoutMs
    ) => {
      clearDeliveryTimeout();
      activeUserMessageIdRef.current = userMessageId;

      timeoutRef.current = setTimeout(() => {
        if (activeUserMessageIdRef.current === userMessageId) {
          activeUserMessageIdRef.current = null;
          onTimeout();
        }
      }, timeoutMs);
    },
    [clearDeliveryTimeout, defaultTimeoutMs]
  );

  const completeDelivery = useCallback(() => {
    clearDeliveryTimeout();
    activeUserMessageIdRef.current = null;
  }, [clearDeliveryTimeout]);

  const isDelivering = useCallback(
    () => activeUserMessageIdRef.current !== null,
    []
  );

  useEffect(() => clearDeliveryTimeout, [clearDeliveryTimeout]);

  return {
    beginDelivery,
    completeDelivery,
    isDelivering,
  };
}
